import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { HosDriverProfileDto, Vehicle } from "../../../../api/vehiclesApi";
import { formatHosTime } from "../../../../utils/dateUtils";

interface ViolationsPanelProps {
  violatingDrivers: Array<{ vehicle: Vehicle; profile: HosDriverProfileDto }>;
}

const ViolationsPanel: React.FC<ViolationsPanelProps> = ({
  violatingDrivers,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();

  if (violatingDrivers.length === 0) return null;

  const handleListWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.scrollTop += event.deltaY;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 0.75,
        bgcolor: "background.paper",
        width: "100%",
        maxHeight: "calc(100vh - 48px)",
        overflow: "hidden",
        opacity: 0.95,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
          HOS Violations ({violatingDrivers.length})
        </Typography>
        <IconButton
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ width: 28, height: 28 }}
        >
          <ExpandMoreIcon
            sx={{
              transform: isExpanded ? "none" : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          />
        </IconButton>
      </Stack>

      <Collapse in={isExpanded} unmountOnExit>
        <Stack
          spacing={0.75}
          onWheelCapture={handleListWheel}
          onTouchMove={(event) => event.stopPropagation()}
          sx={{
            mt: 0.75,
            maxHeight: "calc(100vh - 116px)",
            overflowY: "auto",
            overscrollBehavior: "contain",
            pr: 0.5,
          }}
        >
          {violatingDrivers.map(({ vehicle, profile }) => (
            <Box
              key={vehicle.id}
              sx={{
                p: 0.75,
                border: 1,
                borderColor: "error.main",
                borderRadius: 1,
                bgcolor:
                  theme.palette.mode === "light"
                    ? "status.errorLight"
                    : "status.errorLight",
              }}
            >
              <Typography variant="body2" sx={{ lineHeight: 1.25 }}>
                {vehicle.name}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}
              >
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor:
                      profile.driveTime >= 11 * 60
                        ? "status.errorMain"
                        : "background.paper",
                    color:
                      profile.driveTime >= 11 * 60
                        ? "text.secondary"
                        : theme.palette.mode === "light"
                        ? "text.primary"
                        : "text.primary",
                    fontSize: "0.7rem",
                    border: profile.driveTime >= 11 * 60 ? "none" : "1px solid",
                    borderColor: "divider",
                  }}
                >
                  Drive: {formatHosTime(profile.driveTime)}
                </Box>
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor:
                      profile.shiftTime >= 14 * 60
                        ? "status.errorMain"
                        : "background.paper",
                    color:
                      profile.shiftTime >= 14 * 60
                        ? "text.secondary"
                        : theme.palette.mode === "light"
                        ? "text.primary"
                        : "text.primary",
                    fontSize: "0.7rem",
                    border: profile.shiftTime >= 14 * 60 ? "none" : "1px solid",
                    borderColor: "divider",
                  }}
                >
                  Shift: {formatHosTime(profile.shiftTime)}
                </Box>
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor:
                      profile.cycleTime >= 70 * 60
                        ? "status.errorMain"
                        : "background.paper",
                    color:
                      profile.cycleTime >= 70 * 60
                        ? "text.secondary"
                        : theme.palette.mode === "light"
                        ? "text.primary"
                        : "text.primary",
                    fontSize: "0.7rem",
                    border: profile.cycleTime >= 70 * 60 ? "none" : "1px solid",
                    borderColor: "divider",
                  }}
                >
                  Cycle: {formatHosTime(profile.cycleTime)}
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Paper>
  );
};

export default ViolationsPanel;
